"use client"

import { useCallback, useEffect, useState } from "react"

import api from "@/lib/axios"

/** How long to wait for a challenge before giving up on it. */
const STALL_TIMEOUT_MS = 15_000

/**
 * Turnstile state for a form.
 *
 * The site key is fetched rather than read from a frontend env var — it lives
 * in the backend .env beside the secret, so rotating it needs no rebuild.
 *
 * `blocking` is the value a submit button should disable on. It is false when
 * Turnstile is unconfigured or the widget failed to load, because leaving a
 * reader unable to sign in because a third-party script was blocked is worse
 * than letting the request through — the server still refuses a missing or bad
 * token whenever Turnstile is actually configured.
 */
export function useTurnstile() {
    const [siteKey, setSiteKey] = useState<string | null>(null)
    const [loaded, setLoaded] = useState(false)
    const [token, setToken] = useState<string | null>(null)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        let alive = true
        api.get<{ turnstile_site_key: string | null; enabled: boolean }>("/config/captcha")
            .then((res) => alive && setSiteKey(res.data.enabled ? res.data.turnstile_site_key : null))
            .catch(() => alive && setSiteKey(null))
            .finally(() => alive && setLoaded(true))
        return () => {
            alive = false
        }
    }, [])

    const onVerify = useCallback((t: string) => {
        setToken(t)
        setFailed(false)
    }, [])

    /** A token is single-use; clear it after any submit so a retry re-solves. */
    const reset = useCallback(() => setToken(null), [])

    const onExpire = useCallback(() => setToken(null), [])

    const onError = useCallback(() => {
        setToken(null)
        setFailed(true)
    }, [])

    // The widget can also stall: mounted, no error fired, no token ever issued —
    // seen when a challenge cannot complete. Without this the submit button
    // stays disabled forever and the reader has no way to sign in and no
    // explanation. After the grace period we stop blocking and let the server
    // make the call, exactly as for an outright load failure.
    useEffect(() => {
        if (!siteKey || token || failed) return
        const timer = setTimeout(() => setFailed(true), STALL_TIMEOUT_MS)
        return () => clearTimeout(timer)
    }, [siteKey, token, failed])

    return {
        siteKey,
        loaded,
        token,
        failed,
        blocking: !!siteKey && !token && !failed,
        onVerify,
        onExpire,
        onError,
        reset,
    }
}
